import { isCtaLucideIconKey } from '../../icons/ctaLucideCatalog';
import { parseCtaSocialIconKey } from '../../icons/ctaSocialCatalog';
import type { SiteSocialNetwork } from '../../icons/socialNetworks';

/**
 * Infer messenger brand mark from booking URL (WhatsApp / Facebook / Instagram).
 * Falls back to Lucide when the URL is unknown.
 */
export function resolveMessengerSocialNetwork(
  messengerUrl: string | null | undefined,
): SiteSocialNetwork | null {
  const raw = (messengerUrl ?? '').trim().toLowerCase();
  if (!raw) return null;
  if (
    raw.includes('wa.me') ||
    raw.includes('whatsapp.com') ||
    raw.includes('api.whatsapp') ||
    raw.startsWith('whatsapp:')
  ) {
    return 'whatsapp';
  }
  if (raw.includes('m.me/') || raw.includes('facebook.com') || raw.includes('fb.me')) {
    return 'facebook';
  }
  if (raw.includes('instagram.com') || raw.includes('ig.me')) {
    return 'instagram';
  }
  if (raw.includes('t.me/') || raw.includes('telegram.me') || raw.includes('telegram.org')) {
    return 'telegram';
  }
  return null;
}

/**
 * Resolve messenger CTA mark: explicit `social:` key → Lucide key → URL brand guess.
 * A stored Lucide key wins over URL detection so the picker stays authoritative.
 */
export function resolveMessengerCtaSocial(
  ctaIcon: string | null | undefined,
  messengerUrl: string | null | undefined,
): SiteSocialNetwork | null {
  const fromKey = parseCtaSocialIconKey(ctaIcon);
  if (fromKey) {
    return fromKey;
  }
  /**
   * Legacy default was Lucide MessageCircle — prefer URL brand (WhatsApp) so
   * existing projects show the brand mark without a content migration click.
   */
  if (ctaIcon === 'MessageCircle') {
    return resolveMessengerSocialNetwork(messengerUrl) ?? 'whatsapp';
  }
  if (isCtaLucideIconKey(ctaIcon)) {
    return null;
  }
  return resolveMessengerSocialNetwork(messengerUrl);
}
